package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.TranslationDto
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.components.source.SourceComparison
import dev.lingvo.site.components.source.SourceSnapshotPanel
import dev.lingvo.site.components.source.RetainedTargetPanel
import dev.lingvo.site.components.source.validEarlierSource
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.i18n.t

/** Kept below the writing fields, so asynchronous evidence never moves the caret's paragraph. */
@Composable
internal fun EditorSourceReviewSurface(state: TranslationEditorState, row: KeyRow, draft: EditorDraft) {
    if (!state.stale && !Session.serverFeatures.sourceAwareReview && !draft.sourceClaimCaptured && draft.sourceSnapshot == null && row.sourceSnapshot == null && !state.draftShapeChanged(row, draft)) return
    val current = state.currentSourceFor(row)
    val change = state.sourceReview.loaded(row.key.id)
    val reason = state.sourceSaveProblem(row.key.id)
    val shapeChanged = state.draftShapeChanged(row, draft)
    val supported = Session.serverFeatures.sourceAwareReview && current != null
    var discarding by remember(row.key.id) { mutableStateOf(false) }
    val bridge = LocalEditorDomBridge.current
    LCard(Modifier.fillMaxWidth().attr("data-editor-source-review", "true")
        .then(if (reason != null && state.conflicts[row.key.id] == null) {
            Modifier.attr("id", EDITOR_CONFLICT_TARGET_ID).attr("tabindex", "-1")
        } else Modifier)) {
        Column(Modifier.fillMaxWidth().gap(dev.lingvo.site.ds.Space.MD)) {
            val confirming = state.canonicalAcknowledgements.containsKey(row.key.id) && state.sourceReview.changes[row.key.id] is Async.Loading
            if (confirming) LAlert(
                t("editor.editor_source_review_surface.confirming_saved_source", "Confirming the saved source"),
                t("editor.editor_source_review_surface.your_draft_is_kept_while_the", "Your draft is kept while the complete source is read. Continuing waits for that comparison."),
                variant = LAlertVariant.INFO,
            ) else if (!supported) LAlert(
                t("editor.editor_source_review_surface.source_aware_review_is_unavailable", "Source-aware review is unavailable"),
                unavailableSourceReviewMessage(), variant = LAlertVariant.INFO,
            )
            if (reason != null && supported) LAlert(
                t("editor.editor_source_review_surface.your_draft_is_kept", "Your draft is kept"), reason, variant = LAlertVariant.WARNING,
            )
            SourceComparison(
                current = current,
                earlier = change?.validEarlierSource(),
                // A returned basis describes this returned saved target, not the cached draft.
                savedTarget = if (change == null) row.target else change.target,
                draftBasis = draft.sourceSnapshot,
                showDraftBasis = draft.dirty || reason != null || state.recoveredDrafts[row.key.id] == true,
            )
            if (shapeChanged) {
                LAlert(
                    t("editor.editor_source_review_surface.draft_structure_needs_recovery", "Draft structure needs recovery"),
                    t("editor.editor_source_review_surface.your_original_draft_is_kept_below", "Your original draft is kept below in full. Copy any text you need before deliberately discarding it and opening the updated fields."),
                    variant = LAlertVariant.WARNING,
                )
                RetainedTargetPanel(
                    t("editor.editor_source_review_surface.your_original_draft", "Your original draft"),
                    state.locale,
                    draft.value.takeUnless { draft.retainedShape() == EditorDraftShape.PLURAL },
                    draft.pluralForms.takeIf { draft.retainedShape() in listOf(EditorDraftShape.PLURAL, EditorDraftShape.MIXED) },
                )
                if (state.canWriteTranslations && supported) {
                    LButton(t("editor.editor_source_review_surface.discard_draft_and_start_over", "Discard draft and start over"), { discarding = !discarding },
                        variant = LButtonVariant.SECONDARY)
                    if (discarding) {
                        LAlert(t("editor.editor_source_review_surface.discard_your_unsaved_draft", "Discard your unsaved draft?"),
                            t("editor.editor_source_review_surface.this_removes_its_browser_recovery_copy", "This removes its browser recovery copy. The latest saved translation will remain."),
                            variant = LAlertVariant.WARNING)
                        LButton(t("editor.editor_source_review_surface.discard_mine_take_theirs", "Discard mine, take theirs"), {
                            state.discardDraftForCurrentSource(row.key.id)
                            discarding = false
                        }, variant = LButtonVariant.DANGER, enabled = state.sourceReview.canReview(row.key.id))
                        LButton(t("editor.editor_source_review_surface.cancel", "Cancel"), { discarding = false }, variant = LButtonVariant.GHOST)
                    }
                }
            } else if (reason != null && state.canWriteTranslations && supported &&
                (draft.sourceSnapshot?.view != current?.view || draft.sourceSnapshot == null || !draft.hasTargetClaim() || state.targetReviewNeeded(row.key.id, draft))) {
                LButton(t("editor.editor_source_review_surface.review_current_source_and_continue", "Review current source and continue"), {
                    val target = activeEditorTarget(state)
                    if (state.reviewCurrentSource(row.key.id) && target != null) bridge.revealTargetFragment(target, null)
                }, enabled = state.sourceReview.canReview(row.key.id) && (current?.view?.sourceLocale != state.locale || state.canEditStructure))
            }
            if (state.sourceReview.targetAbsent[row.key.id] == true && state.canWriteTranslations && supported) {
                LAlert(t("editor.editor_source_review_surface.saved_translation_removed", "Saved translation removed"),
                    t("editor.editor_source_review_surface.recreating_uses_your_retained_draft_and", "Recreating uses your retained draft and does not approve it or change its source binding."),
                    variant = LAlertVariant.WARNING)
                LButton(t("editor.editor_source_review_surface.use_my_draft_to_recreate_the", "Use my draft to recreate the translation"), { state.chooseTargetRecreation(row.key.id) })
            }
            if (state.canReaffirm(row, draft)) LButton(
                t("editor.editor_source_review_surface.translation_still_fits_this_source", "Translation still fits this source"), { state.reaffirm(row) },
                enabled = state.sourceReview.reaffirming[row.key.id] != true,
                loading = state.sourceReview.reaffirming[row.key.id] == true,
            )
            if (state.canWriteTranslations && supported && state.sourceReview.pauseCodes[row.key.id] != null &&
                state.sourceReview.pauseCodes[row.key.id] !in listOf("source_changed", "source_view_unavailable", "stale_translation", "translation_conflict") &&
                draft.sourceSnapshot?.view == current?.view && !shapeChanged) {
                LButton(t("editor.editor_source_review_surface.retry_guarded_save", "Retry source-aware save"), { state.retryGuardedSave(row.key.id) },
                    variant = LButtonVariant.SECONDARY)
            }
            if ((state.sourceReview.changes[row.key.id] is Async.Failure || state.sourceReview.changes[row.key.id] is Async.Success && current == null) && Session.serverFeatures.sourceAwareReview) {
                LButton(t("editor.editor_source_review_surface.retry_source_review", "Retry source review"), { state.sourceReview.ensureLoaded(row, retry = true) },
                    variant = LButtonVariant.SECONDARY)
            }
        }
    }
}
